import React, { createContext, useCallback, useEffect, useState } from 'react';
import { debug } from '../client';

export type ThemeMode = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

export interface ThemeContextType {
    theme: ThemeMode;
    resolvedTheme: ResolvedTheme;
    systemTheme: ResolvedTheme;
    setTheme: (theme: ThemeMode) => void;
    toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | null>(null);

export interface ThemeProviderProps {
    children: React.ReactNode;
    defaultTheme?: ThemeMode;
    storageKey?: string;
    attribute?: string;
    enableSystem?: boolean;
}

const STORAGE_KEY_DEFAULT = 'vgrove-theme';
const ATTRIBUTE_DEFAULT = 'data-theme';

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
    children,
    defaultTheme = 'system',
    storageKey = STORAGE_KEY_DEFAULT,
    attribute = ATTRIBUTE_DEFAULT,
    enableSystem = true,
}) => {
    const [theme, setThemeState] = useState<ThemeMode>(defaultTheme);
    const [systemTheme, setSystemTheme] = useState<ResolvedTheme>('light');

    // 获取系统主题
    const getSystemTheme = useCallback((): ResolvedTheme => {
        if (typeof window === 'undefined') return 'light';
        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }, []);

    // 解析当前应该应用的主题
    const resolvedTheme: ResolvedTheme =
        theme === 'system' ? systemTheme : theme as ResolvedTheme;

    // 应用主题到 DOM
    const applyTheme = useCallback((appliedTheme: ResolvedTheme) => {
        const root = document.documentElement;

        // 移除之前的主题类
        root.classList.remove('light', 'dark');
        // 添加新的主题类
        root.classList.add(appliedTheme);
        // 设置 data 属性
        root.setAttribute(attribute, appliedTheme);
    }, [attribute]);

    // 设置主题
    const setTheme = useCallback((newTheme: ThemeMode) => {
        setThemeState(newTheme);

        // 保存到 localStorage
        try {
            localStorage.setItem(storageKey, newTheme);
        } catch (error) {
            debug.warn('Failed to save theme to localStorage:', error);
        }

        // 应用主题
        const applied = newTheme === 'system' ? systemTheme : newTheme as ResolvedTheme;
        applyTheme(applied);
    }, [storageKey, systemTheme, applyTheme]);

    // 切换主题 (light <-> dark，如果是 system 则切换到相反的固定主题)
    const toggleTheme = useCallback(() => {
        if (theme === 'light') {
            setTheme('dark');
        } else if (theme === 'dark') {
            setTheme('light');
        } else {
            // 如果当前是 system，切换到相反的固定主题
            setTheme(systemTheme === 'light' ? 'dark' : 'light');
        }
    }, [theme, systemTheme, setTheme]);

    // 监听系统主题变化
    useEffect(() => {
        if (!enableSystem) return;

        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

        const handleChange = (e: MediaQueryListEvent) => {
            const newSystemTheme = e.matches ? 'dark' : 'light';
            setSystemTheme(newSystemTheme);

            // 如果当前是跟随系统，需要应用新的系统主题
            if (theme === 'system') {
                applyTheme(newSystemTheme);
            }
        };

        // 设置初始系统主题
        setSystemTheme(getSystemTheme());

        // 监听变化
        mediaQuery.addEventListener('change', handleChange);

        return () => {
            mediaQuery.removeEventListener('change', handleChange);
        };
    }, [enableSystem, theme, getSystemTheme, applyTheme]);

    // 初始化主题 - 只在组件挂载时执行一次
    useEffect(() => {
        let initialTheme = defaultTheme;

        // 从 localStorage 读取保存的主题，用户设置的主题优先
        try {
            const savedTheme = localStorage.getItem(storageKey);
            if (savedTheme && ['light', 'dark', 'system'].includes(savedTheme)) {
                initialTheme = savedTheme as ThemeMode;
            }
        } catch (error) {
            debug.warn('Failed to read theme from localStorage:', error);
        }

        setThemeState(initialTheme);

        // 初始化系统主题
        const initialSystemTheme = getSystemTheme();
        setSystemTheme(initialSystemTheme);

        // 应用初始主题
        const appliedTheme = initialTheme === 'system' ? initialSystemTheme : initialTheme as ResolvedTheme;
        applyTheme(appliedTheme);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storageKey, getSystemTheme, applyTheme]); // 移除 defaultTheme 依赖，避免重复初始化

    // 移除响应 defaultTheme 变化的 useEffect
    // 这可能导致用户偏好被意外重置
    // defaultTheme 只在初始化时使用一次

    const contextValue: ThemeContextType = {
        theme,
        resolvedTheme,
        systemTheme,
        setTheme,
        toggleTheme,
    };

    return (
        <ThemeContext.Provider value={contextValue}>
            {children}
        </ThemeContext.Provider>
    );
};

export { ThemeContext }; 